import {useStore} from '@/store/root-store';
import {LoadingOutlined} from '@ant-design/icons';
import {faFeather} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Spin, Tooltip} from 'antd';
import {ContentState, EditorState, Modifier, SelectionState} from 'draft-js';
import {debounce} from 'lodash';
import {observer} from 'mobx-react';
import {useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';

interface Props {
  block?: any;
  htmlToDraft: any;
}

export const WriteForMe = observer(({block, htmlToDraft}: Props) => {
  const {contentOptimizer: {currentPage, contentFragment}} = useStore('');
  const {
    settings: {
      customer: {
        isGHLAccount,
        getCustomerQuota,
        profile: {
          quotaUtilization,
        },
      },
    },
  } = useStore('');

  const [isFullBtn, setIsFullBtn] = useState(true);
  const {
    currentEditorState,
    focusTermsClassNames,
    isSideBarHidden,
  } = currentPage;

  const antIcon = <LoadingOutlined style={{fontSize: 14, marginBottom: 5, height: 14}} spin />;
  const antIconLarge = <LoadingOutlined style={{fontSize: 16, marginBottom: 5, height: 14}} spin />;


  /*
  * Replaces default browser context menu with our custom one
  */
  useEffect(() => {
    const preventingDefaultMenu = e => {
      e.stopPropagation();
    };

    document.querySelectorAll('.writeForMeContainer')?.forEach(elem => {
      if (elem.addEventListener) elem.addEventListener('contextmenu', preventingDefaultMenu, false);
    });

    return () => {
      document.querySelectorAll('.writeForMeContainer')?.forEach(elem => {
        elem.removeEventListener('contextmenu', preventingDefaultMenu, false);
      });
    };
  }, []);


  /*
  * Gets initial Editor actual margins, to use to reposition floating 'people currently editing' label properly
  */
  useEffect(() => {
    setTimeout(() => {
      const editorWrapperElem = document.getElementById('editorContainerId');
      const editorElem = document.querySelectorAll('.DraftEditor-root');
      if (editorElem && editorWrapperElem && editorElem[0]) {
        if ((((editorWrapperElem as HTMLElement).clientWidth - (editorElem[0] as HTMLElement).offsetWidth) / 2) > 110 ) {
          setIsFullBtn(true);
        } else {
          setIsFullBtn(false);
        }
      }
    }, 1000);
  }, [isSideBarHidden]);


  /*
 * Gets Editor actual margins on resize, to use to reposition floating 'people currently editing' label properly
 */
  useEffect(() => {
    const getEditorRealMargin = () => {
      const editorWrapperElem = document.getElementById('editorContainerId');
      const editorElem = document.querySelectorAll('.DraftEditor-root');
      if (editorElem && editorWrapperElem && editorElem[0]) {
        if ((((editorWrapperElem as HTMLElement).clientWidth - (editorElem[0] as HTMLElement).offsetWidth) / 2) > 110 ) {
          setIsFullBtn(true);
        } else {
          setIsFullBtn(false);
        }
      }
    };
    window.addEventListener('resize', getEditorRealMargin);

    return () => window.removeEventListener('resize', getEditorRealMargin);
  }, []);


  const debouncedChange = debounce((state: EditorState, focusTerms, focusTermsClassNames, isNewDecorations = false) => {
    // we are then calling the edit content method which will fire the API call and send new state to the BE
    currentPage.editContent(state, isNewDecorations);
  }, 2000);


  const debouncedUpdate = useCallback(
    (state: EditorState, focusTerms, focusTermsClassNames, isNewDecorations) => debouncedChange(state, focusTerms, focusTermsClassNames, isNewDecorations),
    // eslint-disable-next-line
        []
  );


  /*
  * Gets current Editor selected text
  */
  const getSelection = useCallback((editorState: EditorState) => {
    return editorState.getSelection();
  }, [currentPage.currentEditorState]);


  /*
  * Inserts AI generated text into Editor State
  */
  const insertText = async (isBlock: boolean) => {
    const {contentBlocks, entityMap} = htmlToDraft(contentFragment?.fragment?.html || '');

    const selection = currentPage.currentEditorState.getSelection();


    // this is used to make selection for the entire block, if we are using entire block text to generate ai content;
    const newSelection = isBlock ?
      new SelectionState({
        anchorKey: block.getKey(),
        anchorOffset: block.getText().length,
        focusKey: block.getKey(),
        focusOffset: block.getText().length,
      }) :
      new SelectionState({
        anchorKey: selection.getAnchorKey(),
        anchorOffset: selection.getEndOffset() !== 0 ? selection.getEndOffset() : block.getText().length,
        focusKey: selection.getAnchorKey(),
        focusOffset: selection.getEndOffset() !== 0 ? selection.getEndOffset() : block.getText().length,
      });


    const fragment = ContentState.createFromBlockArray(contentBlocks, entityMap).getBlockMap();

    const nonStyledState = Modifier.replaceWithFragment(
      currentPage.currentEditorState.getCurrentContent(),
      newSelection,
      fragment,
    );

    // WE are calculating nodes to toggle blue background on new Ai generated nodes
    let firstNewBlockId: number;
    const currentBlock = selection.getAnchorKey();
    const nextBlock = currentPage.currentEditorState.getCurrentContent().getBlockAfter(selection.getAnchorKey())?.getKey();

    const existingNodes = document.querySelectorAll('[data-block="true"]');
    existingNodes?.forEach((elem, id) => {
      if (elem.getAttribute('data-offset-key') && elem.getAttribute('data-offset-key') == `${currentBlock}-0-0`) {
        firstNewBlockId = id + 1;
      }
    });


    const updatedState = EditorState.push(currentPage.currentEditorState, nonStyledState, 'insert-fragment');
    currentPage.setCurrentEditorState(updatedState);

    currentPage.setAiTooltip({show: false});

    setTimeout(() => {
      const newTextElems = document.querySelectorAll('[data-block="true"]');
      if (newTextElems.length) {
        for (let i = firstNewBlockId; i <= newTextElems.length; i++) {
          if (newTextElems[i]?.getAttribute('data-offset-key') && newTextElems[i]?.getAttribute('data-offset-key') != `${nextBlock}-0-0` && newTextElems[i]?.getAttribute('data-offset-key') != `${currentBlock}-0-0`) {
            newTextElems[i].classList.add('newTextBlock');
          } else {
            return;
          }
        }
      }
    }, 500);

    debouncedUpdate(updatedState, currentPage.analytics?.focusTerms, focusTermsClassNames, false);

    setTimeout(() => {
      const newTextElems = document.querySelectorAll('.newTextBlock');
      if (newTextElems.length) {
        newTextElems.forEach(elem => {
          elem.classList.remove('newTextBlock');
        });
      }
    }, 3500);
  };

  const getParams = (editorState: EditorState) => {
    const selection = getSelection(editorState);
    const currentContent = editorState.getCurrentContent();
    const anchorKey = selection.getAnchorKey();

    return {selection, currentContent, anchorKey};
  };


  /*
  * Fetching either selected text or entire block to generate AI content
  * Triggering Editor text inserting method
  */
  const fetchAndInsertText = async (action: 'insert' | 'copy') => {
    const {selection, currentContent, anchorKey} = getParams(currentEditorState);
    const start = selection.getAnchorOffset() < selection.getFocusOffset() ? selection.getAnchorOffset() : selection.getFocusOffset();
    const end = selection.getFocusOffset() > selection.getAnchorOffset() ? selection.getFocusOffset() : selection.getAnchorOffset();

    const currentBlock = currentContent.getBlockForKey(anchorKey);

    let selectedText: string;
    let isBlock: boolean = false;

    if (!selection.isCollapsed() && anchorKey === block.getKey()) {
      selectedText = currentBlock.getText().slice(start, end);

      // This will handle tripple click
      // In draftJS, triple click results in anchor and focus ofset to be 0
      if (selection.getAnchorOffset() === 0 && selection.getFocusOffset() === 0) {
        selectedText = currentBlock.getText();
      }
    }

    if (selection.isCollapsed() && anchorKey === block.getKey()) {
      isBlock = true;
      selectedText = block.getText();
    }

    if (selection.isCollapsed() && anchorKey !== block.getKey()) {
      isBlock = true;
      selectedText = block.getText();
    }

    if (!selection.isCollapsed() && anchorKey !== block.getKey()) {
      isBlock = true;
      selectedText = block.getText();
    }

    try {
      await contentFragment.fetchFragment(selectedText);
      getCustomerQuota();
      if (action === 'insert') {
        insertText(isBlock);
      }

      // if (action === 'copy') {
      //   currentPage.setAiTooltip({...currentPage.aiTooltip, show: false});
      //   if (contentFragment?.fragment?.html) {
      //     navigator.clipboard.writeText(contentFragment?.fragment?.html);
      //     currentPage.setIsCopy(true);

      //     setTimeout(() => {
      //       currentPage.setIsCopy(false);
      //     }, 5000);
      //   }
      // }
    } catch (e) {
      currentPage.setAiTooltip({...currentPage.aiTooltip, show: false});
      Promise.reject(e);
    }
  };


  /*
  * Checks if there is any selected text
  */
  const isTextSelected = () => {
    const {selection, anchorKey} = getParams(currentEditorState);

    if (!selection.isCollapsed() && anchorKey === block.getKey()) {
      return true;
    } else {
      return false;
    }
  };


  /*
  * Checks if Block has any text or not
  */
  const isEmptyContent = () => {
    const {selection} = getParams(currentEditorState);

    if (selection.isCollapsed() && !block.getText().length) {
      return true;
    } else {
      return false;
    }
  };

  return <Container>
    {isFullBtn ? <Tooltip title={isEmptyContent() ? 'Write some text to generate AI content.' : `AI Generate content based on ${isTextSelected() ? 'selected text' : 'entire block'}.`}>
      <WriteForMeStyled style={isEmptyContent() ? {cursor: 'not-allowed'} : {}} className='writeForMeBtn' onClick={() => {
        if (isEmptyContent()) return;
        fetchAndInsertText('insert');
      }}>
        {contentFragment.fragmentLoading ? <Spin style={{height: 14, marginTop: -10, marginRight: 6}} indicator={antIcon} /> :
          <FontAwesomeIcon icon={faFeather} className='writeForMeFeatherIcon' style={{color: '#7F4EAD', width: 20, height: 14, display: 'inline-block'}}/>}
      </WriteForMeStyled>
    </Tooltip> :
      <Tooltip title={isEmptyContent() ? 'Write some text to generate AI content.' : `AI Generate content based on ${isTextSelected() ? 'selected text' : 'entire block'}.`}>
        <WriteForMeMiniStyled className='writeForMeBtn' style={isEmptyContent() ? {cursor: 'not-allowed'} : {}} onClick={e => {
          if (isEmptyContent()) return;
          e.stopPropagation();
          fetchAndInsertText('insert');
        }}>
          {contentFragment.fragmentLoading ? <Spin style={{height: 14, marginTop: -10, marginRight: 6}} indicator={antIconLarge} /> :
            <FontAwesomeIcon icon={faFeather} className='writeForMeFeatherIcon' style={{color: '#7F4EAD', marginRight: 5, height: 17, width: 17, marginBottom: 'auto', display: 'block'}}/>}
        </WriteForMeMiniStyled>
      </Tooltip> }
    {(isGHLAccount === false) && isFullBtn ? <BasedOn isSelection={isTextSelected()}></BasedOn> : null}
    {(isGHLAccount === false) && isFullBtn ? <UsedQuota
      total={quotaUtilization?.ca?.allowedAiContentGeneration?.total}
      used={quotaUtilization?.ca?.allowedAiContentGeneration?.consumed}>
    </UsedQuota> : null}
  </Container>;
});

const Container = styled.div`
  height: 100%;
`;

const WriteForMeStyled = styled.div`
  font-size: 12px;
  background-color: #E6DFEF;
  height: 26px;
  cursor: pointer;
  margin-right: auto;
  width: 105px;
  color: #7F4EAD;
  padding: 4px 7px;
  position: relative;
  border-radius: 5px;
  font-weight: 500;

  &::after {
    position: absolute;
    content: 'Write for me'
  }
`;

const WriteForMeMiniStyled = styled.div`
  background-color: #E6DFEF;
  height: 26px;
  cursor: pointer;
  margin-left: auto;
  margin-right: 20px;
  width: 30px;
  color: #7F4EAD;
  border-radius: 5px;
  position: relative;
  padding-top: 5px;
  padding-left: 6px;
`;

const BasedOn = styled.div<{isSelection: boolean}>`
  font-size: 10px;
  pointer-events: none;

  &::after {
    position: absolute;
    content: 'Based on ${p => p.isSelection ? 'selected text' : 'entire block'}';
  }
`;
const UsedQuota = styled.div<{used?: number; total?: number}>`
  font-size: 10px;
  pointer-events: none;
  margin-top: 12px;
  &::after {
    position: absolute;
    content: 'Quota ${p => `${p.used}/${p.total}`}';
  }
`;
